// Linked deals (aggregator feature `linked_deals`, LMG profile).
// Two tabs: payments to confirm across the WHOLE book (Approved vs Settled only
// shows its recent window), active loans first; and the accuracy check that
// gates writing automatic links back to MyCRM. Decisions go through
// decideDealLoanLink; this page never decides on its own.
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Link2 } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { SkeletonTable } from '../../components/common/SkeletonLoader';
import { FindPaymentPanel } from '../../components/reports/avsV2/FindPaymentPanel';
import { useAppContext } from '../../components/layout/AppLayout';
import { decideAwaitingPayment } from '../../hooks/reports/useAwaitingPayment';
import {
  canUseLinkedDeals,
  useLinkedDealsOverview,
  useLinkedDealsSample,
  useLinkedDealsToConfirm,
  type LinkedDealRow,
  type SampleRow,
} from '../../hooks/reports/useLinkedDeals';
import { formatCompactCurrency } from '../../utils/formatters';
import { DEFAULT_REPORT_PATH } from '../../config/reportRegistry';

const PAGE_SIZE = 50;
const EMPTY = '-';

type Tab = 'confirm' | 'accuracy';

function pct(x: number | null): string {
  return x === null ? EMPTY : `${(x * 100).toFixed(1)}%`;
}

/** Hidden (commission-restricted) or unknown amounts show as a dash, never $0. */
function money(cents: number | null | undefined): string {
  return cents === null || cents === undefined ? EMPTY : formatCompactCurrency(cents);
}

const ActivePill: React.FC<{ active: boolean }> = ({ active }) =>
  active ? (
    <span className="inline-flex text-[0.6875rem] font-semibold px-2 py-0.5 rounded-full border bg-emerald-400/10 text-emerald-300 border-emerald-400/40">
      Still paying trail
    </span>
  ) : (
    <span className="inline-flex text-[0.6875rem] font-semibold px-2 py-0.5 rounded-full border bg-slate-400/10 text-slate-400 border-slate-500/40">
      No recent trail
    </span>
  );

/** One deal and its loan(s), side by side: MyCRM on the left, the commission statement on the right. */
const DealPair: React.FC<{ row: LinkedDealRow }> = ({ row }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500">MyCRM deal {row.dealId}</p>
      <p className="font-medium">{row.borrowerName ?? EMPTY}</p>
      <p className="text-sm text-slate-400">
        {row.lender ?? EMPTY} · settled {row.settledDate ?? EMPTY} · {money(row.dealAmountCents)}
      </p>
    </div>
    <div className="space-y-2">
      {row.loans.map((l) => (
        <div key={l.loanId}>
          <p className="text-xs uppercase tracking-wide text-slate-500">Commission loan {l.loanId}</p>
          <p className="font-medium">{l.customerName ?? EMPTY}</p>
          <p className="text-sm text-slate-400 flex flex-wrap items-center gap-2">
            <span>
              {l.lender ?? EMPTY} · settled {l.settlementDate ?? EMPTY} · {money(l.loanAmountCents)}
            </span>
            <ActivePill active={l.active} />
          </p>
        </div>
      ))}
    </div>
  </div>
);

const LinkedDealsPage: React.FC = () => {
  const ctx = useAppContext() as {
    userData?: ({ role?: string; companyId?: string } & Record<string, unknown>) | null;
    companyData?: { id?: string; aggregator?: unknown; featureFlags?: Record<string, unknown> } | null;
  };
  const companyId = ctx.companyData?.id ?? ctx.userData?.companyId ?? null;
  const allowed = canUseLinkedDeals(ctx.userData, ctx.companyData);
  const canFind = ctx.companyData?.featureFlags?.approved_vs_settled_v2 === 'enabled';

  const [tab, setTab] = useState<Tab>('confirm');
  const [offset, setOffset] = useState(0);
  const [activeOnly, setActiveOnly] = useState(true);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [finding, setFinding] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const overview = useLinkedDealsOverview(allowed ? companyId : null, tick);
  const toConfirm = useLinkedDealsToConfirm(allowed && tab === 'confirm' ? companyId : null, offset, PAGE_SIZE, activeOnly, tick);
  const sample = useLinkedDealsSample(allowed && tab === 'accuracy' ? companyId : null, tick);

  // Decisions shrink the list: a page past the end comes back empty, so step back.
  useEffect(() => {
    if (offset > 0 && toConfirm.data && toConfirm.data.deals.length === 0) setOffset(Math.max(0, offset - PAGE_SIZE));
  }, [offset, toConfirm.data]);

  if (!allowed) return <Navigate to={DEFAULT_REPORT_PATH} replace />;

  const decide = async (row: LinkedDealRow, action: 'confirm' | 'dismiss', done: string) => {
    if (!companyId) return;
    setBusy(row.dealId);
    setMessage(null);
    try {
      await decideAwaitingPayment({ companyId, dealId: row.dealId, action, loanIds: row.loans.map((l) => l.loanId) });
      setMessage(done);
      setTick((t) => t + 1);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'That did not save. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const ptc = overview.data?.byState.payment_to_confirm;
  const auto = overview.data?.byState.auto;
  const confirmed = overview.data?.byState.confirmed;
  const summary = sample.data?.summary;

  const tabBtn = (key: Tab, label: string) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === key ? 'bg-blue-600/20 text-blue-300 border border-blue-500/40' : 'text-slate-400 hover:text-slate-200'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Link2}
        title="Linked deals"
        subtitle="Match MyCRM deals to the loans your lenders pay commission on. Every confirmed link gets its loan number in MyCRM the next morning."
        onRefresh={() => setTick((t) => t + 1)}
        isRefreshing={overview.loading}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-4">
          <p className="text-sm text-slate-400">Payments to confirm</p>
          <p className="text-2xl font-semibold">{ptc?.deals ?? EMPTY}</p>
          <p className="text-xs text-slate-500">{ptc ? `${ptc.activeDeals} still paying trail` : ''}</p>
        </div>
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-4">
          <p className="text-sm text-slate-400">Linked automatically</p>
          <p className="text-2xl font-semibold">{auto?.deals ?? EMPTY}</p>
          <p className="text-xs text-slate-500">{auto ? `${auto.activeDeals} still paying trail` : ''}</p>
        </div>
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-4">
          <p className="text-sm text-slate-400">Confirmed by your team</p>
          <p className="text-2xl font-semibold">{confirmed?.deals ?? EMPTY}</p>
        </div>
      </div>

      <div className="flex gap-2">
        {tabBtn('confirm', 'Payments to confirm')}
        {tabBtn('accuracy', 'Accuracy check')}
      </div>

      {message && <p className="text-sm text-slate-300">{message}</p>}

      {tab === 'confirm' && (
        <section className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-400">
              Every deal where VALUTTEN has a likely payment but is not sure, across all years. Loans still paying trail come first.
            </p>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => {
                  setActiveOnly(e.target.checked);
                  setOffset(0);
                }}
              />
              Still paying trail only
            </label>
          </div>
          {toConfirm.loading && <SkeletonTable rows={6} />}
          {toConfirm.error && <p className="text-sm text-red-300">{toConfirm.error}</p>}
          {!toConfirm.loading && toConfirm.data && toConfirm.data.deals.length === 0 && (
            <p className="text-sm text-slate-400">Nothing to confirm.</p>
          )}
          {!toConfirm.loading &&
            toConfirm.data?.deals.map((row) => (
              <div key={row.dealId} className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-4 space-y-3">
                <DealPair row={row} />
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={busy === row.dealId}
                    onClick={() => decide(row, 'confirm', 'Payment confirmed.')}
                    className="px-3 py-1.5 rounded-lg bg-blue-600 text-sm disabled:opacity-40"
                  >
                    Confirm payment
                  </button>
                  <button
                    type="button"
                    disabled={busy === row.dealId}
                    onClick={() => decide(row, 'dismiss', 'Payment dismissed.')}
                    className="px-3 py-1.5 rounded-lg border border-slate-600 text-sm disabled:opacity-40"
                  >
                    Dismiss
                  </button>
                  {canFind && (
                    <button
                      type="button"
                      onClick={() => setFinding(finding === row.dealId ? null : row.dealId)}
                      className="px-3 py-1.5 rounded-lg border border-slate-600 text-sm"
                    >
                      Find the payment
                    </button>
                  )}
                </div>
                {finding === row.dealId && companyId && (
                  <FindPaymentPanel
                    companyId={companyId}
                    dealId={row.dealId}
                    onCancel={() => setFinding(null)}
                    onLinked={() => {
                      setFinding(null);
                      setMessage('Payment linked.');
                      setTick((t) => t + 1);
                    }}
                  />
                )}
              </div>
            ))}
          {toConfirm.data && toConfirm.data.total > PAGE_SIZE && (
            <div className="flex items-center gap-3 text-sm text-slate-400">
              <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} className="disabled:opacity-40">
                Previous
              </button>
              <span>
                {offset + 1} to {Math.min(offset + PAGE_SIZE, toConfirm.data.total)} of {toConfirm.data.total}
              </span>
              <button
                type="button"
                disabled={offset + PAGE_SIZE >= toConfirm.data.total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
                className="disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </section>
      )}

      {tab === 'accuracy' && (
        <section className="space-y-4">
          <p className="text-sm text-slate-400">
            A fixed random sample of automatic links. Mark each one Correct or Wrong against MyCRM. When every link is reviewed, the
            result shows how accurate automatic linking is, which decides whether automatic links can be written to MyCRM too.
          </p>
          {sample.data?.notReady === 'too_few' && (
            <p className="text-sm text-slate-400">
              The accuracy check needs {sample.data.needed} automatic links and this account has {sample.data.autoLinks ?? 0} so far. It
              starts once there are enough.
            </p>
          )}
          {sample.data?.notReady === 'stale' && (
            <p className="text-sm text-slate-400">The accuracy check is being reset. Contact VALUTTEN support.</p>
          )}
          {summary && (
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-4">
              <p className="font-medium">
                {summary.reviewed} of {summary.size} reviewed · {summary.wrong} wrong
              </p>
              <p className="text-sm text-slate-400">
                {summary.reviewed === 0
                  ? 'No links reviewed yet.'
                  : `With 95% confidence the error rate is at most ${pct(summary.errorRateUpper95)}${summary.reviewed < summary.size ? ' so far' : ''}.`}
              </p>
            </div>
          )}
          {sample.loading && <SkeletonTable rows={6} />}
          {sample.error && <p className="text-sm text-red-300">{sample.error}</p>}
          {!sample.loading &&
            sample.data?.deals.map((row: SampleRow) => (
              <div key={row.dealId} className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-4 space-y-3">
                <DealPair row={row} />
                {row.status === 'pending' ? (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={busy === row.dealId}
                      onClick={() => decide(row, 'confirm', 'Marked correct.')}
                      className="px-3 py-1.5 rounded-lg bg-emerald-600 text-sm disabled:opacity-40"
                    >
                      Correct
                    </button>
                    <button
                      type="button"
                      disabled={busy === row.dealId}
                      onClick={() => decide(row, 'dismiss', 'Marked wrong.')}
                      className="px-3 py-1.5 rounded-lg border border-red-500/60 text-red-300 text-sm disabled:opacity-40"
                    >
                      Wrong
                    </button>
                  </div>
                ) : (
                  <p className={`text-sm ${row.status === 'correct' ? 'text-emerald-300' : 'text-red-300'}`}>
                    {row.status === 'correct' ? 'Reviewed: correct' : 'Reviewed: wrong'}
                  </p>
                )}
              </div>
            ))}
        </section>
      )}
    </div>
  );
};

export default LinkedDealsPage;
